<script setup>
/**
 * Desktop Toolbar Component
 * Contains the "Start" button and other toolbar items
 */

defineProps({
  showZones: {
    type: Boolean,
    default: true,
  },
})

defineEmits(['open-start', 'toggle-zones', 'open-settings'])
</script>

<template>
  <div class="desktop-toolbar">
    <button class="toolbar-btn start-btn" @click="$emit('open-start')">
      <svg class="start-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
      </svg>
      <span class="start-text">הוסף קיצור</span>
    </button>

    <button
      class="toolbar-btn zones-btn"
      :class="{ active: showZones }"
      @click="$emit('toggle-zones')"
      :title="showZones ? 'הסתר אזורי מכשירים' : 'הצג אזורי מכשירים'"
    >
      <svg
        class="zones-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
      >
        <rect x="3" y="4" width="7" height="16" rx="1.5" />
        <rect x="12" y="6" width="9" height="12" rx="1.5" />
      </svg>
      <span class="zones-text">אזורים</span>
    </button>

    <div class="toolbar-spacer"></div>

    <span class="toolbar-hint mobile-hint">לחץ לפתיחה | לחיצה ארוכה למחיקה</span>

    <button class="toolbar-btn settings-btn" @click="$emit('open-settings')" title="הגדרות">
      <svg class="settings-icon" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.desktop-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid #e2e8f0;
  min-height: 48px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.start-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: #f1f5f9;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.start-btn:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.start-icon {
  width: 18px;
  height: 18px;
}

.zones-btn {
  display: none;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: #f1f5f9;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.zones-btn.active {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.4);
}

.zones-btn:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.zones-icon {
  width: 18px;
  height: 18px;
}

@media (min-width: 1025px) {
  .zones-btn {
    display: flex;
  }
}

.toolbar-spacer {
  flex: 1;
}

.settings-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  background: #f1f5f9;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.settings-btn:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.settings-icon {
  width: 18px;
  height: 18px;
}

.mobile-hint {
  display: none;
  color: #64748b;
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .mobile-hint {
    display: inline;
  }

  .start-btn {
    padding: 0.6rem 1rem;
  }
}
</style>
